import React, { useState, useRef } from 'react';

/**
 * Reusable Cloudinary Image Uploader
 * @param {Function} onUploadSuccess - Callback with the secure URL on success
 * @param {Function} onError - Callback with error details on failure
 * @param {string} buttonText - Text to display on the button
 * @param {string} className - Optional additional CSS classes
 */
const ImageUploader = ({ 
  onUploadSuccess, 
  onError, 
  buttonText = "Upload Image",
  className = "",
  oldUrl = null,
  folder = "centre-uploads"
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;

    setIsUploading(true);
    
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';
    const token = localStorage.getItem('amps_token');
    
    let url = `${baseUrl}/upload/image?folder=${folder}`;
    if (oldUrl) {
      url += `&oldUrl=${encodeURIComponent(oldUrl)}`;
    }

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const result = await response.json();

      if (result.success && result.data?.url) {
        onUploadSuccess(result.data.url);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error("Image Upload Error:", err);
      const errorMessage = err.message || "Sacred Image Upload Failed. Please try again.";
      if (onError) onError(errorMessage);
      else alert(errorMessage);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleUpload(file);
    }
  };

  const triggerFileInput = () => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`inline-block ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      
      <button
        type="button"
        onClick={triggerFileInput}
        disabled={isUploading}
        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] transition-all duration-300 shadow-lg active:scale-95
          ${isUploading 
            ? 'bg-stone-800 text-stone-500 cursor-not-allowed border border-white/5' 
            : 'bg-[#F0C75E] text-black hover:bg-white hover:text-[#2E2A5C] shadow-[#F0C75E]/20'}`}
      >
        {isUploading ? (
          <>
            <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>
            Uploading to Cloud...
          </>
        ) : (
          <>
            <span className="material-symbols-outlined text-[18px]">cloud_upload</span>
            {buttonText}
          </>
        )}
      </button>
    </div>
  );
};

export default ImageUploader;
